"use client";

import { ClerkLoaded, ClerkLoading, Show, SignInButton, SignOutButton } from "@clerk/nextjs";
import { ChevronRight } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Logo } from "@/components/logo";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { MARKETING_LANDING_SECTION_IDS } from "@/lib/marketing/landing-anchors";
import { cn, scrollToElementById } from "@/lib/utils";

const NAV_ITEMS: ReadonlyArray<{ id: string; label: string }> = [
  { id: MARKETING_LANDING_SECTION_IDS.problem, label: "Why" },
  { id: MARKETING_LANDING_SECTION_IDS.features, label: "Pillars" },
  { id: MARKETING_LANDING_SECTION_IDS.differentiator, label: "Compare" },
  { id: MARKETING_LANDING_SECTION_IDS.howItWorks, label: "How it works" },
  { id: MARKETING_LANDING_SECTION_IDS.integrations, label: "Integrations" },
  { id: MARKETING_LANDING_SECTION_IDS.pricing, label: "Pricing" },
  { id: MARKETING_LANDING_SECTION_IDS.faq, label: "FAQ" },
];

type MarketingHeaderProps = {
  className?: string;
};

export function MarketingHeader({ className }: MarketingHeaderProps) {
  const [isAppLoading, setIsAppLoading] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 border-border/60 border-b bg-background/75 backdrop-blur-md",
        className,
      )}
    >
      <div className="mx-auto flex h-16 w-full max-w-(--breakpoint-2xl) items-center justify-between gap-3 px-4">
        <div className="flex items-center gap-3">
          <Logo />
          <nav aria-label="Page sections" className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map((item) => (
              <Button
                key={item.id}
                onClick={() => scrollToElementById(item.id)}
                size="sm"
                type="button"
                variant="ghost"
              >
                {item.label}
              </Button>
            ))}
            <Button asChild size="sm" type="button" variant="ghost">
              <Link
                className="flex items-center gap-2"
                href="https://docs.linguolink.dev"
                rel="noopener noreferrer"
                target="_blank"
              >
                Docs
              </Link>
            </Button>
          </nav>
        </div>

        <ClerkLoading>
          <Button size="sm" variant="outline">
            Sign in
          </Button>
        </ClerkLoading>
        <ClerkLoaded>
          <div className="flex items-center gap-2">
            <Show
              fallback={
                <SignInButton mode="modal">
                  <Button size="sm" variant="outline">
                    Sign in
                  </Button>
                </SignInButton>
              }
              when="signed-in"
            >
              <div className="flex items-center gap-2">
                <SignOutButton>
                  <Button
                    disabled={isSigningOut}
                    onClick={() => setIsSigningOut(true)}
                    size="sm"
                    variant="outline"
                  >
                    {isSigningOut ? (
                      <>
                        <Spinner className="mr-1 size-3" />
                        Signing out…
                      </>
                    ) : (
                      "Sign out"
                    )}
                  </Button>
                </SignOutButton>
                <Button asChild size="sm" variant="outline">
                  <Link
                    className="flex items-center gap-2"
                    href="/app"
                    onClick={() => setIsAppLoading(true)}
                  >
                    App
                    {isAppLoading ? (
                      <Spinner className="ml-1 size-3" />
                    ) : (
                      <ChevronRight className="size-4" />
                    )}
                  </Link>
                </Button>
              </div>
            </Show>
          </div>
        </ClerkLoaded>
      </div>
    </header>
  );
}
